import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";

const CATEGORY_META = {
  invisible: { label: "Invisible", color: "#eb5a5a", bg: "#1a0808" },
  underweighted: { label: "Underweighted", color: "#ba7a30", bg: "#1a0f08" },
  unfocused: { label: "Unfocused", color: "#bba030", bg: "#14140a" },
  unpackaged: { label: "Unpackaged", color: "#3aab5a", bg: "#0a1a0a" },
  unamplified: { label: "Unamplified", color: "#FF6575", bg: "#1a0a08" },
};

const CATEGORY_OPTS = Object.entries(CATEGORY_META).map(([value, m]) => ({ value, label: m.label }));

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const categoryBadge = (cat) => {
  const meta = CATEGORY_META[cat];
  if (!meta) return <span style={{ color: "#666", fontSize: 11 }}>—</span>;
  return (
    <span
      style={{
        background: meta.bg,
        color: meta.color,
        fontSize: 10,
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.06em",
        padding: "3px 10px",
        borderRadius: 20,
        whiteSpace: "nowrap",
      }}
    >
      {meta.label}
    </span>
  );
};

export const InterviewSubmissions = ({ adminKey, onUnauthorized }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ positioning_contains: "", category: "" });
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.positioning_contains) params.positioning_contains = filters.positioning_contains;
      if (filters.category) params.category = filters.category;
      const res = await api.get("/admin/interview-submissions", {
        params,
        headers: { "X-TAG-Admin-Key": adminKey },
        validateStatus: () => true,
      });
      if (res.status === 401) {
        onUnauthorized();
        return;
      }
      setRows(res.data || []);
    } catch {
      toast.error("Failed to load interview submissions.");
    } finally {
      setLoading(false);
    }
  }, [adminKey, filters, onUnauthorized]);

  useEffect(() => {
    // Load once on mount. Filter changes are applied via the Apply button (matches ScoreSubmissions pattern).
    load();
  }, []);

  const counts = useMemo(() => {
    const c = { total: rows.length };
    Object.keys(CATEGORY_META).forEach((k) => (c[k] = 0));
    rows.forEach((r) => {
      const k = r.authority_score_category;
      if (c[k] !== undefined) c[k]++;
    });
    return c;
  }, [rows]);

  return (
    <div data-testid="interview-submissions">
      {/* Summary */}
      <div
        style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 8, marginBottom: 20 }}
        className="is-summary"
      >
        <Stat label="Total" value={counts.total} color="#fff" />
        {Object.entries(CATEGORY_META).map(([k, m]) => (
          <Stat key={k} label={m.label} value={counts[k]} color={m.color} />
        ))}
      </div>

      {/* Filters */}
      <div className="tag-card" style={{ marginBottom: 20, padding: 18 }}>
        <div
          style={{ display: "grid", gridTemplateColumns: "2fr 1fr auto", gap: 10, alignItems: "end" }}
          className="is-filters"
        >
          <Field label="Positioning statement contains">
            <input
              data-testid="is-filter-positioning"
              type="text"
              className="tag-input"
              value={filters.positioning_contains}
              onChange={(e) => setFilters((f) => ({ ...f, positioning_contains: e.target.value }))}
              onKeyDown={(e) => e.key === "Enter" && load()}
              placeholder="e.g. compliance, licensure, IEP"
            />
          </Field>
          <Field label="Authority category">
            <select
              data-testid="is-filter-category"
              className="tag-input"
              value={filters.category}
              onChange={(e) => setFilters((f) => ({ ...f, category: e.target.value }))}
            >
              <option value="">All categories</option>
              {CATEGORY_OPTS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </Field>
          <div style={{ display: "flex", gap: 8 }}>
            <button data-testid="is-apply-filters" type="button" className="btn-primary" onClick={load}>
              Apply
            </button>
            <button
              data-testid="is-reset-filters"
              type="button"
              className="btn-ghost"
              onClick={() => {
                setFilters({ positioning_contains: "", category: "" });
                setTimeout(load, 0);
              }}
            >
              Reset
            </button>
          </div>
        </div>
      </div>

      {/* Header row */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <h2 style={{ color: "#fff", fontSize: 16, fontWeight: 700, margin: 0 }}>
          Completed interviews{rows.length > 0 && <span style={{ color: "#777", fontWeight: 500 }}>{` (${rows.length})`}</span>}
        </h2>
        <button data-testid="is-refresh" type="button" onClick={load} className="btn-ghost">↻ Refresh</button>
      </div>

      {loading && <div className="tag-card" style={{ textAlign: "center", color: "#777" }}>Loading…</div>}

      {!loading && rows.length === 0 && (
        <div className="tag-card" data-testid="is-empty" style={{ textAlign: "center", color: "#bbb" }}>
          No completed interviews yet. When someone finishes the /interview flow they&apos;ll appear here.
        </div>
      )}

      {!loading && rows.length > 0 && (
        <div className="tag-card" style={{ padding: 0, overflow: "hidden" }}>
          <div style={{ overflowX: "auto" }}>
            <table
              style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, color: "#bbb", minWidth: 900 }}
              data-testid="is-table"
            >
              <thead>
                <tr style={{ background: "#0d0d0d", borderBottom: "1px solid #1a1a1a" }}>
                  {["Email", "Date", "Positioning Statement", "Category", "Source", ""].map((h) => (
                    <th
                      key={h}
                      style={{
                        textAlign: "left",
                        padding: "12px 14px",
                        color: "#888",
                        fontSize: 10,
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: "0.06em",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const isOpen = expanded === r.session_id;
                  return (
                    <React.Fragment key={r.session_id}>
                      <tr
                        data-testid={`is-row-${r.session_id}`}
                        style={{ borderBottom: "1px solid #161616", verticalAlign: "top" }}
                      >
                        <td style={{ padding: "12px 14px", color: "#fff", whiteSpace: "nowrap" }}>{r.email || "—"}</td>
                        <td style={{ padding: "12px 14px", color: "#777", whiteSpace: "nowrap" }}>{fmtDate(r.created_at)}</td>
                        <td style={{ padding: "12px 14px", maxWidth: 380 }}>
                          <span
                            style={{
                              color: "#ddd",
                              fontStyle: r.positioning_statement ? "italic" : "normal",
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                            }}
                          >
                            {r.positioning_statement ? `“${r.positioning_statement}”` : "—"}
                          </span>
                        </td>
                        <td style={{ padding: "12px 14px" }}>{categoryBadge(r.authority_score_category)}</td>
                        <td style={{ padding: "12px 14px", color: "#bbb", whiteSpace: "nowrap" }}>
                          {r.utm_source || "—"}
                          {r.utm_campaign && <div style={{ color: "#666", fontSize: 10 }}>{r.utm_campaign}</div>}
                        </td>
                        <td style={{ padding: "12px 14px", whiteSpace: "nowrap" }}>
                          <button
                            data-testid={`is-toggle-${r.session_id}`}
                            type="button"
                            onClick={() => setExpanded(isOpen ? null : r.session_id)}
                            style={{
                              background: "transparent",
                              border: "1px solid #222",
                              color: "#bbb",
                              borderRadius: 6,
                              padding: "5px 10px",
                              cursor: "pointer",
                              fontSize: 11,
                              fontFamily: "var(--font-body)",
                            }}
                          >
                            {isOpen ? "Hide" : "View"}
                          </button>
                        </td>
                      </tr>
                      {isOpen && (
                        <tr style={{ background: "#0b0b0b", borderBottom: "1px solid #161616" }}>
                          <td colSpan={6} style={{ padding: "18px 22px" }} data-testid={`is-detail-${r.session_id}`}>
                            <div
                              style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 22 }}
                              className="is-detail-grid"
                            >
                              <div>
                                <DetailItem label="Pattern insight" value={r.pattern_insight} multiline />
                                <DetailItem label="Gap moment" value={r.gap_moment} multiline />
                                <DetailItem label="Proof (their voice)" value={r.proof_quote ? `“${r.proof_quote}”` : ""} multiline italic />
                              </div>
                              <div>
                                <DetailItem label="Audience segment" value={r.audience_segment} multiline />
                                <DetailItem
                                  label="Positioning statement"
                                  value={r.positioning_statement ? `“${r.positioning_statement}”` : ""}
                                  multiline
                                  italic
                                />
                                <DetailItem label="Authority category" value={r.authority_score_category || ""} />
                                <DetailItem label="Completed" value={fmtDate(r.completed_at)} />
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <style>{`
        @media (max-width: 900px) {
          .is-summary { grid-template-columns: repeat(3, 1fr) !important; }
          .is-filters { grid-template-columns: 1fr !important; }
          .is-detail-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
};

const Stat = ({ label, value, color }) => (
  <div className="tag-card" style={{ padding: "14px 16px" }}>
    <div style={{ color, fontSize: 22, fontWeight: 700, lineHeight: 1.1 }}>{value}</div>
    <div
      style={{
        color: "#888",
        fontSize: 10,
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.06em",
        marginTop: 4,
      }}
    >
      {label}
    </div>
  </div>
);

const Field = ({ label, children }) => (
  <div>
    <label
      style={{
        display: "block",
        color: "#888",
        fontSize: 10,
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.06em",
        marginBottom: 6,
      }}
    >
      {label}
    </label>
    {children}
  </div>
);

const DetailItem = ({ label, value, multiline, italic }) => (
  <div style={{ marginBottom: 10 }}>
    <div
      style={{
        color: "#FF6575",
        fontSize: 9,
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.08em",
        marginBottom: 4,
      }}
    >
      {label}
    </div>
    <div
      style={{
        color: "#ddd",
        fontSize: 12,
        lineHeight: multiline ? 1.6 : 1.4,
        whiteSpace: multiline ? "pre-wrap" : "normal",
        fontStyle: italic ? "italic" : "normal",
      }}
    >
      {value || "—"}
    </div>
  </div>
);
